import type { Feature } from "@unbox/api";
import { useMemo } from "react";
import { useListData } from "react-stately";
import { toast } from "sonner";
import { useUpdateMerchant } from "@/api/admin/update-merchant";
import { MultiSelect } from "@/components/base/select/multi-select";
import { ALL_FEATURES, FEATURE_LABELS } from "@/utils/features";
import { TabHeader } from "./tab-header";
import type { MerchantWithUsers } from "./types";

type PermissionsTabProps = {
  merchant: MerchantWithUsers;
};

const PermissionsForm = ({ merchant }: PermissionsTabProps) => {
  const updateMerchant = useUpdateMerchant();

  const selectedFeatures = useListData<{ id: Feature; label: string }>({
    initialItems: merchant.features.map((f) => ({ id: f, label: FEATURE_LABELS[f] })),
  });

  const initialFeatureIds = useMemo(() => new Set(merchant.features), [merchant.features]);

  const currentFeatureIds = useMemo(() => new Set(selectedFeatures.items.map((f) => f.id)), [selectedFeatures.items]);

  const hasChanges = useMemo(() => {
    if (initialFeatureIds.size !== currentFeatureIds.size) return true;
    for (const id of initialFeatureIds) {
      if (!currentFeatureIds.has(id)) return true;
    }
    return false;
  }, [initialFeatureIds, currentFeatureIds]);

  const handleSave = async () => {
    try {
      await updateMerchant.mutateAsync({
        merchantId: merchant.id,
        name: merchant.name,
        features: selectedFeatures.items.map((f) => f.id),
      });
      toast.success("Permissions updated successfully");
    } catch {
      toast.error("Failed to update permissions");
    }
  };

  const resetFeatures = () => {
    // Remove items not in merchant.features
    const toRemove = selectedFeatures.items.filter((f) => !merchant.features.includes(f.id));
    for (const item of toRemove) {
      selectedFeatures.remove(item.id);
    }
    // Add items in merchant.features that are not in selectedFeatures
    const currentIds = new Set(selectedFeatures.items.map((f) => f.id));
    for (const f of merchant.features) {
      if (!currentIds.has(f)) {
        selectedFeatures.append({ id: f, label: FEATURE_LABELS[f] });
      }
    }
  };

  const handleCancel = () => {
    resetFeatures();
  };

  return (
    <div className="flex flex-col gap-6">
      <TabHeader
        title="Permissions"
        subtitle="These are the areas of the platform we are granting to the user"
        hasChanges={hasChanges}
        isPending={updateMerchant.isPending}
        onSave={handleSave}
        onCancel={handleCancel}
      />

      <div className="border-t border-primary" />

      <div className="flex flex-row gap-8">
        <div className="w-80 flex flex-col gap-1">
          <p className="text-sm font-medium text-primary">
            Platform Access <span className="text-error">*</span>
          </p>
          <p className="text-sm text-tertiary">
            This is a list of all the features this merchant will have permissions to see
          </p>
        </div>
        <div className="flex-1 max-w-lg">
          <MultiSelect
            aria-label="Features"
            placeholder="Select features"
            size="md"
            items={ALL_FEATURES.map((f) => ({ id: f, label: FEATURE_LABELS[f] }))}
            selectedItems={selectedFeatures}
          >
            {(item) => (
              <MultiSelect.Item key={item.id} id={item.id}>
                {item.label}
              </MultiSelect.Item>
            )}
          </MultiSelect>
        </div>
      </div>
    </div>
  );
};

export const PermissionsTab = ({ merchant }: PermissionsTabProps) => (
  <PermissionsForm key={merchant.id} merchant={merchant} />
);
